import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  CloudSun, Cloud, CloudRain, CloudSnow, Sun, CloudLightning, CloudDrizzle, Cloudy,
  Activity, BarChart3, StickyNote, Cake, Timer, Clock, Headphones, ExternalLink, Rss,
  FileText, Users, BookOpen, FolderOpen, ArrowUpRight, Gift
} from 'lucide-react';
import { Card, CardContent } from './ui/card';

const API = '/api';

const WMO_ICONS = {
  0: Sun, 1: Sun, 2: CloudSun, 3: Cloudy,
  45: Cloud, 48: Cloud,
  51: CloudDrizzle, 53: CloudDrizzle, 55: CloudDrizzle,
  61: CloudRain, 63: CloudRain, 65: CloudRain,
  71: CloudSnow, 73: CloudSnow, 75: CloudSnow,
  80: CloudRain, 81: CloudRain, 82: CloudRain,
  95: CloudLightning, 96: CloudLightning, 99: CloudLightning,
};
const WMO_DESC = {
  0: "Jasno", 1: "Převážně jasno", 2: "Polojasno", 3: "Zataženo",
  45: "Mlha", 48: "Mlha", 51: "Mrholení", 53: "Mrholení", 55: "Mrholení",
  61: "Déšť", 63: "Déšť", 65: "Silný déšť", 71: "Sněžení", 73: "Sněžení", 75: "Silné sněžení",
  80: "Přeháňky", 81: "Přeháňky", 82: "Silné přeháňky",
  95: "Bouřka", 96: "Bouřka s krupobitím", 99: "Bouřka s krupobitím",
};

function WeatherWidget({ config }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.get(`${API}/widgets/weather`, { withCredentials: true })
      .then(r => setData(r.data)).catch(() => {});
  }, []);
  if (!data?.current) return <WidgetShell title={config?.city || "Počasí"} icon={CloudSun} loading />;
  const code = data.current.weather_code;
  const WeatherIcon = WMO_ICONS[code] || Cloud;
  const temp = Math.round(data.current.temperature_2m);
  const desc = WMO_DESC[code] || "—";
  const daily = data.daily || {};
  return (
    <WidgetShell title={data.city || "Počasí"} icon={WeatherIcon}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-3xl font-bold text-[#052E16]">{temp}°C</p>
          <p className="text-xs text-[#57534E] mt-0.5">{desc}</p>
          <p className="text-xs text-[#57534E]">Vlhkost {data.current.relative_humidity_2m}% · Vítr {Math.round(data.current.wind_speed_10m)} km/h</p>
        </div>
        <WeatherIcon className="w-12 h-12 text-[#F59E0B]" />
      </div>
      {daily.time && daily.time.length > 1 && (
        <div className="flex gap-2 mt-3 pt-3 border-t border-[#E2E8F0]">
          {daily.time.slice(1, 3).map((d, i) => {
            const DayIcon = WMO_ICONS[daily.weather_code?.[i + 1]] || Cloud;
            return (
              <div key={d} className="flex-1 text-center p-1.5 rounded-lg bg-[#F8FAF8]">
                <p className="text-[10px] text-[#57534E]">{new Date(d).toLocaleDateString('cs-CZ', { weekday: 'short' })}</p>
                <DayIcon className="w-4 h-4 mx-auto my-1 text-[#57534E]" />
                <p className="text-xs font-medium text-[#1C1917]">{Math.round(daily.temperature_2m_max?.[i + 1])}°/{Math.round(daily.temperature_2m_min?.[i + 1])}°</p>
              </div>
            );
          })}
        </div>
      )}
    </WidgetShell>
  );
}

function StatsWidget() {
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.get(`${API}/widgets/stats`, { withCredentials: true })
      .then(r => setData(r.data)).catch(() => {});
  }, []);
  if (!data) return <WidgetShell title="Statistiky" icon={BarChart3} loading />;
  const items = [
    { icon: Users, label: "Uživatelé", value: data.users, color: "text-[#16A34A]" },
    { icon: BookOpen, label: "Články", value: data.posts, color: "text-[#3B82F6]" },
    { icon: FileText, label: "Dokumenty", value: data.documents, color: "text-[#F59E0B]" },
    { icon: FolderOpen, label: "Složky", value: data.folders, color: "text-[#8B5CF6]" },
  ];
  return (
    <WidgetShell title="Statistiky" icon={BarChart3}>
      <div className="grid grid-cols-2 gap-2">
        {items.map(item => (
          <div key={item.label} className="p-2.5 rounded-lg bg-[#F8FAF8] text-center">
            <item.icon className={`w-4 h-4 mx-auto ${item.color}`} />
            <p className="text-lg font-bold text-[#052E16] mt-1">{item.value}</p>
            <p className="text-[10px] text-[#57534E]">{item.label}</p>
          </div>
        ))}
      </div>
    </WidgetShell>
  );
}

function RecentActivityWidget() {
  const [data, setData] = useState([]);
  useEffect(() => {
    axios.get(`${API}/widgets/recent-activity`, { withCredentials: true })
      .then(r => setData(r.data)).catch(() => {});
  }, []);
  const typeConfig = {
    blog: { icon: BookOpen, label: "Článek", color: "text-[#3B82F6]" },
    document: { icon: FileText, label: "Dokument", color: "text-[#F59E0B]" },
    user: { icon: Users, label: "Uživatel", color: "text-[#16A34A]" },
  };
  return (
    <WidgetShell title="Poslední aktivita" icon={Activity}>
      {data.length > 0 ? (
        <div className="space-y-2">
          {data.slice(0, 5).map((item, i) => {
            const cfg = typeConfig[item.type] || typeConfig.blog;
            return (
              <div key={i} className="flex items-center gap-2 text-xs">
                <cfg.icon className={`w-3.5 h-3.5 flex-shrink-0 ${cfg.color}`} />
                <span className="truncate text-[#1C1917]">{item.title}</span>
                <span className="ml-auto text-[#57534E] flex-shrink-0 text-[10px]">
                  {item.date ? new Date(item.date).toLocaleDateString('cs-CZ') : ''}
                </span>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-xs text-[#57534E] text-center py-2">Žádná aktivita</p>
      )}
    </WidgetShell>
  );
}

function MemoWidget({ config }) {
  const text = config?.text;
  return (
    <WidgetShell title="Firemní poznámka" icon={StickyNote}>
      {text ? (
        <p className="text-sm text-[#1C1917] leading-relaxed whitespace-pre-wrap">{text}</p>
      ) : (
        <p className="text-xs text-[#57534E] italic text-center py-2">Žádná poznámka</p>
      )}
    </WidgetShell>
  );
}

function NamedayWidget() {
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.get(`${API}/widgets/nameday`, { withCredentials: true })
      .then(r => setData(r.data)).catch(() => {});
  }, []);
  if (!data) return <WidgetShell title="Svátky" icon={Cake} loading />;
  return (
    <WidgetShell title="Svátky" icon={Cake}>
      <div className="space-y-2">
        <div className="flex items-center justify-between p-2.5 rounded-lg bg-[#FEF3C7]">
          <div>
            <p className="text-[10px] text-[#92400E] font-medium">Dnes {data.today.date}</p>
            <p className="text-sm font-semibold text-[#92400E]">{data.today.name}</p>
          </div>
          <Gift className="w-5 h-5 text-[#F59E0B]" />
        </div>
        <div className="flex items-center justify-between p-2.5 rounded-lg bg-[#F8FAF8]">
          <div>
            <p className="text-[10px] text-[#57534E]">Zítra {data.tomorrow.date}</p>
            <p className="text-sm font-medium text-[#1C1917]">{data.tomorrow.name}</p>
          </div>
        </div>
      </div>
    </WidgetShell>
  );
}

function CountdownWidget({ config }) {
  const label = config?.label || "Událost";
  const dateStr = config?.date;
  if (!dateStr) return (
    <WidgetShell title="Odpočet" icon={Timer}>
      <p className="text-xs text-[#57534E] italic text-center py-2">Datum není nastaveno</p>
    </WidgetShell>
  );
  const target = new Date(dateStr);
  const now = new Date();
  const diffMs = target - now;
  const days = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  return (
    <WidgetShell title="Odpočet" icon={Timer}>
      <div className="text-center py-1">
        <p className="text-3xl font-bold text-[#16A34A]">{days > 0 ? days : 0}</p>
        <p className="text-xs text-[#57534E]">{days > 0 ? (days === 1 ? 'den' : days < 5 ? 'dny' : 'dní') : 'Událost proběhla'}</p>
        <p className="text-sm font-medium text-[#052E16] mt-1">{label}</p>
        <p className="text-[10px] text-[#57534E]">{target.toLocaleDateString('cs-CZ', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
      </div>
    </WidgetShell>
  );
}

function WorldClockWidget({ config }) {
  const [times, setTimes] = useState({});
  const zones = (config?.zones || "Europe/Prague").split(",").map(z => z.trim());
  useEffect(() => {
    const update = () => {
      const t = {};
      zones.forEach(z => {
        try { t[z] = new Date().toLocaleTimeString('cs-CZ', { timeZone: z, hour: '2-digit', minute: '2-digit' }); }
        catch { t[z] = '--:--'; }
      });
      setTimes(t);
    };
    update();
    const id = setInterval(update, 30000);
    return () => clearInterval(id);
  }, [config?.zones]);
  const zoneLabels = { "Europe/Prague": "Praha", "America/New_York": "New York", "Asia/Tokyo": "Tokio", "Europe/London": "Londýn", "Asia/Dubai": "Dubaj", "Australia/Sydney": "Sydney" };
  return (
    <WidgetShell title="Světové hodiny" icon={Clock}>
      <div className="space-y-1.5">
        {zones.map(z => (
          <div key={z} className="flex items-center justify-between p-2 rounded-lg bg-[#F8FAF8]">
            <span className="text-xs text-[#57534E]">{zoneLabels[z] || z.split('/').pop()}</span>
            <span className="text-sm font-mono font-semibold text-[#052E16]">{times[z] || '--:--'}</span>
          </div>
        ))}
      </div>
    </WidgetShell>
  );
}

function ITSupportWidget({ config }) {
  return (
    <WidgetShell title="IT Podpora" icon={Headphones}>
      <div className="space-y-2 text-sm">
        {config?.phone && <p className="text-[#1C1917]"><span className="text-[#57534E]">Tel:</span> {config.phone}</p>}
        {config?.email && <p className="text-[#1C1917]"><span className="text-[#57534E]">E-mail:</span> {config.email}</p>}
        {config?.hours && <p className="text-[#1C1917]"><span className="text-[#57534E]">Hodiny:</span> {config.hours}</p>}
        {!config?.phone && !config?.email && <p className="text-xs text-[#57534E] italic text-center">Kontakt není nastaven</p>}
      </div>
    </WidgetShell>
  );
}

function QuickLinksWidget({ config }) {
  const items = (config?.items || "").split("\n").filter(l => l.includes("|")).map(l => {
    const [name, url] = l.split("|").map(s => s.trim());
    return { name, url };
  });
  return (
    <WidgetShell title="Rychlé odkazy" icon={ExternalLink}>
      {items.length > 0 ? (
        <div className="space-y-1.5">
          {items.map((item, i) => (
            <a key={i} href={item.url} target="_blank" rel="noopener noreferrer"
              className="flex items-center justify-between p-2 rounded-lg bg-[#F8FAF8] hover:bg-[#F0FDF4] transition-colors text-xs text-[#1C1917] group">
              {item.name}
              <ArrowUpRight className="w-3 h-3 text-[#57534E] group-hover:text-[#16A34A]" />
            </a>
          ))}
        </div>
      ) : (
        <p className="text-xs text-[#57534E] italic text-center py-2">Žádné odkazy</p>
      )}
    </WidgetShell>
  );
}

function RssWidget({ config }) {
  return (
    <WidgetShell title={config?.title || "RSS Feed"} icon={Rss}>
      <p className="text-xs text-[#57534E] italic text-center py-2">
        {config?.url ? "RSS feed se načítá..." : "URL feedu není nastavena"}
      </p>
    </WidgetShell>
  );
}

function WidgetShell({ title, icon: Icon, children, loading }) {
  return (
    <Card className="bg-white border-[#E2E8F0] shadow-sm overflow-hidden">
      <div className="px-4 py-2.5 border-b border-[#E2E8F0] flex items-center gap-2">
        <Icon className="w-4 h-4 text-[#16A34A]" />
        <span className="text-sm font-semibold text-[#052E16] font-['Manrope']">{title}</span>
      </div>
      <CardContent className="p-4">
        {loading ? (
          <div className="flex items-center justify-center py-4">
            <div className="w-5 h-5 border-2 border-[#16A34A] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : children}
      </CardContent>
    </Card>
  );
}

const WIDGET_COMPONENTS = {
  weather: WeatherWidget,
  recent_activity: RecentActivityWidget,
  stats: StatsWidget,
  memo: MemoWidget,
  nameday: NamedayWidget,
  countdown: CountdownWidget,
  clock: WorldClockWidget,
  it_support: ITSupportWidget,
  links: QuickLinksWidget,
  rss: RssWidget,
};

export default function WidgetSidebar() {
  const [widgets, setWidgets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/widgets`, { withCredentials: true })
      .then(r => setWidgets(r.data.filter(w => w.enabled)))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading || widgets.length === 0) return null;

  return (
    <div className="space-y-4" data-testid="widget-sidebar">
      {widgets.map((widget, index) => {
        const WidgetComponent = WIDGET_COMPONENTS[widget.type];
        if (!WidgetComponent) return null;
        return (
          <motion.div
            key={widget.id}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.08 }}
            data-testid={`widget-${widget.type}`}
          >
            <WidgetComponent config={widget.config} />
          </motion.div>
        );
      })}
    </div>
  );
}
